import React, { useMemo, useState, useEffect, useCallback } from 'react';
import Sidebar from './common/Sidebar';
import Header from './common/Header';
import { useTranslation } from 'react-i18next';
import { useFetch } from '../hooks/useFetch';
import './Transfers.css';


// Helper: CLP formatting by locale
const fmtMoney = (n, locale, currency = 'CLP') =>
  Number(n || 0).toLocaleString(locale, { style: 'currency', currency });

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const endOfYear = (d) => new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);

const Transfers = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'es';
  const locale = lang.startsWith('es') ? 'es-CL' : lang.startsWith('en') ? 'en-US' : lang;
  const CURRENCY = 'CLP';

  // Period filters
  const [period, setPeriod] = useState('month'); // 'month' | 'year' | 'all'
  const [monthRef, setMonthRef] = useState(() => startOfMonth(new Date()));

  // Extra filters (shared for both tables)
  const [qRecipient, setQRecipient] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // Sorting (independent for Sent / Received)
  const [sortSent, setSortSent] = useState({ key: 'date', dir: 'desc' });
  const [sortRecv, setSortRecv] = useState({ key: 'date', dir: 'desc' });

  const toggleSort = (current, key) => {
    if (current.key === key) {
      return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
    }
    return { key, dir: 'asc' };
  };

  const cmp = (a, b, key, dir) => {
    let av = a?.[key];
    let bv = b?.[key];
    if (key === 'amount') {
      av = Number(av) || 0; bv = Number(bv) || 0;
    } else if (key === 'date') {
      av = a?.date ? new Date(a.date).getTime() : 0;
      bv = b?.date ? new Date(b.date).getTime() : 0;
    } else {
      av = (av ?? '').toString().toLowerCase();
      bv = (bv ?? '').toString().toLowerCase();
    }
    const r = av < bv ? -1 : av > bv ? 1 : 0;
    return dir === 'asc' ? r : -r;
  };

  // Data from expenses; we detect transfers by keywords in description/merchant/note
  // Expected shape (flexible): { expenses: [{ id, date, amount, description, merchant, note, type }] } or an array
  const [expensesData] = useFetch('/transactions?include=all', { defaultValue: { expenses: [] }, defaultLimit: 500 });

  const rawExpenses = Array.isArray(expensesData?.transactions) ? expensesData.transactions
                     : (Array.isArray(expensesData) ? expensesData : []);

  // Build transfer-like rows from expenses
  const kwTransfer = /(transfer|transferencia|transf|trf|ttf|traspaso)/i;
  const kwReceivedHints = /(abono|dep[oó]sito|recibido|entrada)/i;

  const mapExpenseToRow = (e) => {
    const text = `${e?.description || ''} ${e?.note || ''} ${e?.merchant || ''}`.toLowerCase();
    if (!kwTransfer.test(text)) return null;

    const amt = Number(e?.amount) || 0;
    let dir = amt < 0 ? 'sent' : amt > 0 ? 'received' : (kwReceivedHints.test(text) ? 'received' : 'sent');

    const base = {
      id: e?.id,
      date: e?.date,
      note: e?.description || e?.note || '',
      amount: Math.abs(amt)
    };

    if (dir === 'sent') {
      return { ...base, to: e?.merchant || e?.counterparty || '—', _dir: 'sent' };
    }
    return { ...base, from: e?.merchant || e?.counterparty || '—', _dir: 'received' };
  };

  const transferRows = rawExpenses.map(mapExpenseToRow).filter(Boolean);
  const rawSent = transferRows.filter(r => r._dir === 'sent').map(({ _dir, ...rest }) => rest);
  const rawReceived = transferRows.filter(r => r._dir === 'received').map(({ _dir, ...rest }) => rest);

  // Date window based on period
  const dateWindow = useMemo(() => {
    const base = monthRef instanceof Date ? monthRef : new Date();
    if (period === 'month') return { from: startOfMonth(base), to: endOfMonth(base) };
    if (period === 'year') return { from: startOfYear(base), to: endOfYear(base) };
    return { from: null, to: null };
  }, [period, monthRef]);

  // Month navigation for period=month
  const prevMonth = () => setMonthRef((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const nextMonth = () => setMonthRef((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));

  // Normalize date ref on period switch
  useEffect(() => {
    if (period === 'month') setMonthRef((m) => startOfMonth(m || new Date()));
    if (period === 'year') setMonthRef((m) => startOfYear(m || new Date()));
  }, [period]);

  const periodLabel = useMemo(() => {
    if (period === 'month') return monthRef.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    if (period === 'year') return monthRef.toLocaleDateString(locale, { year: 'numeric' });
    return t('common.all', 'Todos');
  }, [period, monthRef, locale, t]);

  // Shared row predicate according to filters/period
  const rowFilter = useCallback((row, kind) => {
    const d = row?.date ? new Date(row.date) : null;
    if (dateWindow.from && d && d < dateWindow.from) return false;
    if (dateWindow.to && d && d > dateWindow.to) return false;

    if (qRecipient) {
      const hay = `${kind === 'sent' ? (row?.to || '') : (row?.from || '')} ${row?.note || ''}`.toLowerCase();
      if (!hay.includes(qRecipient.toLowerCase())) return false;
    }

    const amt = Number(row?.amount) || 0;
    if (minAmount !== '' && amt < Number(minAmount)) return false;
    return !(maxAmount !== '' && amt > Number(maxAmount));
  }, [dateWindow, qRecipient, minAmount, maxAmount]);
  const filteredSent = useMemo(() => rawSent.filter((r) => rowFilter(r, 'sent')), [rawSent, rowFilter]);
  const sortedSent = useMemo(() => {
    const { key, dir } = sortSent; return [...filteredSent].sort((a, b) => cmp(a, b, key, dir));
  }, [filteredSent, sortSent]);

  const filteredRecv = useMemo(() => rawReceived.filter((r) => rowFilter(r, 'received')), [rawReceived, rowFilter]);
  const sortedRecv = useMemo(() => {
    const { key, dir } = sortRecv; return [...filteredRecv].sort((a, b) => cmp(a, b, key, dir));
  }, [filteredRecv, sortRecv]);


  return (
    <div className="dashboard-container overview">
      <Header title={t('transfers.title', 'Transferencias')} />
      <div className="dashboard-four-columns">
        <Sidebar />
        <div className="dashboard-main">

          {/* Header: Period Selector */}
          <div className="dashboard-section section-wide" style={{ gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="transfers-toggle-group">
                  <button
                    type="button"
                    className={period === 'month' ? 'active' : ''}
                    onClick={() => setPeriod('month')}
                    aria-pressed={period === 'month'}
                  >
                    {t('common.month', 'Mes')}
                  </button>
                  <button
                    type="button"
                    className={period === 'year' ? 'active' : ''}
                    onClick={() => setPeriod('year')}
                    aria-pressed={period === 'year'}
                  >
                    {t('common.year', 'Año')}
                  </button>
                  <button
                    type="button"
                    className={period === 'all' ? 'active' : ''}
                    onClick={() => setPeriod('all')}
                    aria-pressed={period === 'all'}
                  >
                    {t('common.all', 'Todos')}
                  </button>
                </div>

                {period === 'month' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button onClick={prevMonth}>◀</button>
                    <strong style={{ minWidth: 140, textTransform: 'capitalize' }}>{periodLabel}</strong>
                    <button onClick={nextMonth}>▶</button>
                  </div>
                )}

                {period === 'year' && (
                  <strong style={{ minWidth: 80 }}>{periodLabel}</strong>
                )}
              </div>
            </div>
          </div>

          {/* Filters (shared for both tables) */}
          <div className="dashboard-section section-wide" style={{ gridColumn: '1 / -1', marginTop: 8 }}>
            <div className="dashboard-filters-row inside-bar transfers-filters-row">
              {/* Buscar primero (izquierda, ancho flexible) */}
              <div className="dashboard-cardtype-filter" style={{ minWidth: 240, flex: '1 1 340px' }}>
                <label className="dashboard-cardtype-label">{t('transfers.filterRecipient', 'Buscar')}</label>
                <input
                  className="dashboard-cardtype-select"
                  type="text"
                  value={qRecipient}
                  onChange={(e) => setQRecipient(e.target.value)}
                  placeholder={t('transfers.searchRecipient', 'Filtrar por nombre o nota')}
                />
              </div>

              {/* Monto mín. */}
              <div className="dashboard-cardtype-filter" style={{ width: 180 }}>
                <label className="dashboard-cardtype-label">{t('transfers.minAmount', 'Monto mín.')}</label>
                <input
                  className="dashboard-cardtype-select"
                  type="number"
                  min="0"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  placeholder="0"
                />
              </div>

              {/* Monto máx. */}
              <div className="dashboard-cardtype-filter" style={{ width: 180 }}>
                <label className="dashboard-cardtype-label">{t('transfers.maxAmount', 'Monto máx.')}</label>
                <input
                  className="dashboard-cardtype-select"
                  type="number"
                  min="0"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  placeholder=""
                />
              </div>

              {(qRecipient || minAmount || maxAmount) && (
                <button className="pretty-button secondary" onClick={() => { setQRecipient(''); setMinAmount(''); setMaxAmount(''); }}>
                  {t('common.clear', 'Limpiar')}
                </button>
              )}
            </div>
          </div>

          {/* Dual tables */}
          <div className="dashboard-section section-wide" style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Sent */}
            <div className="dashboard-table-wrapper" style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
              <div style={{ padding: '8px 12px', fontWeight: 600 }}>{t('transfers.sent', 'Enviadas')}</div>
              <table className="dashboard-expenses-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th onClick={() => setSortSent((s) => toggleSort(s, 'date'))} style={{ textAlign: 'left', cursor: 'pointer' }}>
                      {t('transfers.col.date', 'Fecha')} {sortSent.key === 'date' ? (sortSent.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th onClick={() => setSortSent((s) => toggleSort(s, 'to'))} style={{ textAlign: 'left', cursor: 'pointer' }}>
                      {t('transfers.col.to', 'Hacia')} {sortSent.key === 'to' ? (sortSent.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th onClick={() => setSortSent((s) => toggleSort(s, 'note'))} style={{ textAlign: 'left', cursor: 'pointer' }}>
                      {t('transfers.note', 'Nota')} {sortSent.key === 'note' ? (sortSent.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th onClick={() => setSortSent((s) => toggleSort(s, 'amount'))} style={{ textAlign: 'right', cursor: 'pointer' }}>
                      {t('transfers.col.amount', 'Monto')} {sortSent.key === 'amount' ? (sortSent.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rawSent.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', opacity: 0.6, padding: '12px 8px' }}>{t('transfers.empty', 'Sin transferencias')}</td></tr>
                  ) : (
                    sortedSent.map((r) => {
                      const d = r?.date ? new Date(r.date) : null;
                      return (
                        <tr key={`s-${r.id || `${r.date}-${r.to}-${r.amount}`}`}>
                          <td>{d ? d.toLocaleDateString(locale) : '—'}</td>
                          <td style={{ whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r?.to || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r?.note || '—'}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoney(r?.amount, locale, CURRENCY)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Received */}
            <div className="dashboard-table-wrapper" style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
              <div style={{ padding: '8px 12px', fontWeight: 600 }}>{t('transfers.received', 'Recibidas')}</div>
              <table className="dashboard-expenses-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th onClick={() => setSortRecv((s) => toggleSort(s, 'date'))} style={{ textAlign: 'left', cursor: 'pointer' }}>
                      {t('transfers.col.date', 'Fecha')} {sortRecv.key === 'date' ? (sortRecv.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th onClick={() => setSortRecv((s) => toggleSort(s, 'from'))} style={{ textAlign: 'left', cursor: 'pointer' }}>
                      {t('transfers.col.from', 'Desde')} {sortRecv.key === 'from' ? (sortRecv.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th onClick={() => setSortRecv((s) => toggleSort(s, 'note'))} style={{ textAlign: 'left', cursor: 'pointer' }}>
                      {t('transfers.note', 'Nota')} {sortRecv.key === 'note' ? (sortRecv.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th onClick={() => setSortRecv((s) => toggleSort(s, 'amount'))} style={{ textAlign: 'right', cursor: 'pointer' }}>
                      {t('transfers.col.amount', 'Monto')} {sortRecv.key === 'amount' ? (sortRecv.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rawReceived.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', opacity: 0.6, padding: '12px 8px' }}>{t('transfers.empty', 'Sin transferencias')}</td></tr>
                  ) : (
                    sortedRecv.map((r) => {
                      const d = r?.date ? new Date(r.date) : null;
                      return (
                        <tr key={`r-${r.id || `${r.date}-${r.from}-${r.amount}`}`}>
                          <td>{d ? d.toLocaleDateString(locale) : '—'}</td>
                          <td style={{ whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r?.from || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r?.note || '—'}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoney(r?.amount, locale, CURRENCY)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Transfers;